import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PROJECT_MANAGEMENT_URL } from '@insite-store360/shared';
import { Observable, catchError, map, of } from 'rxjs';
import { ErrorHandlerService } from './error-handler.service';

export interface DropdownOption {
  id: number | string;
  name?: string;
  label?: string;
  [key: string]: any;
}

export interface KPIData {
  currencyCode?: string;
  created: { count: number; value: number };
  submittedForApproval: { count: number; value: number };
  confirmedQuotation: { count: number; value: number };
  lostLeads: { count: number; value: number };
}

export interface MonthlySummaryData {
  labels: string[];
  leadsCreated: number[];
  confirmedQuotation: number[];
}

export interface AnalyticalKPIData {
  bidding: number;
  awarded: number;
  nsDelivered: number;
  sellInQuantity?: number;
  lost: number;
  ctfAwarded?: number;
  ctfDelivered?: number;
  currencyCode?: string;
  hasSapData?: boolean;
}

export interface YearWiseData {
  years: number[];
  bidding: number[];
  awarded: number[];
  nsDelivered: number[];
  sellInQuantity?: number[];
  lost: number[];
  target: number[];
  totalProject: number[];
  currencyCode?: string;
}

export interface CategoryWiseData {
  label: string;
  value: number;
  quantity: number;
}

@Injectable({
  providedIn: 'root',
})
export class ProjectManagementDashboardService {
  private readonly URL_PATH = `${PROJECT_MANAGEMENT_URL}/dashboard`;

  constructor(
    private http: HttpClient,
    private errorHandler: ErrorHandlerService
  ) { }

  /**
   * Get KPI data for dashboard
   */
  public getKPIData(countryId?: number, monthIds?: number[], yearIds?: number[], salesPersonIds?: number[], targetCurrency?: string): Observable<KPIData> {
    let params = new HttpParams();
    if (countryId !== undefined && countryId !== null) {
      params = params.set('countryId', countryId.toString());
    }
    if (monthIds && monthIds.length > 0) {
      params = params.set('monthIds', monthIds.join(','));
    }
    if (yearIds && yearIds.length > 0) {
      params = params.set('yearIds', yearIds.join(','));
    }
    if (salesPersonIds && salesPersonIds.length > 0) {
      params = params.set('salesPersonIds', salesPersonIds.join(','));
    }
    if (targetCurrency) {
      params = params.set('targetCurrency', targetCurrency);
    }
    return this.http
      .get<KPIData>(`${this.URL_PATH}/kpi`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get monthly lead summary data for chart
   */
  public getMonthlyLeadSummary(countryId?: number, monthIds?: number[], yearIds?: number[], salesPersonIds?: number[], targetCurrency?: string): Observable<MonthlySummaryData> {
    let params = new HttpParams();
    if (countryId !== undefined && countryId !== null) {
      params = params.set('countryId', countryId.toString());
    }
    if (monthIds && monthIds.length > 0) {
      params = params.set('monthIds', monthIds.join(','));
    }
    if (yearIds && yearIds.length > 0) {
      params = params.set('yearIds', yearIds.join(','));
    }
    if (salesPersonIds && salesPersonIds.length > 0) {
      params = params.set('salesPersonIds', salesPersonIds.join(','));
    }
    if (targetCurrency) {
      params = params.set('targetCurrency', targetCurrency);
    }
    return this.http
      .get<MonthlySummaryData>(`${this.URL_PATH}/monthly-summary`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get project sales list data for the table
   */
  public getProjectSalesList(
    page: number = 1,
    limit: number = 50,
    regionIds?: number[],
    countryIds?: number[],
    yearIds?: number[],
    monthIds?: number[],
    quarterFilters?: string[],
    search?: string,
    statusNames?: string[],
    customerNames?: string[],
    productTypes?: string[],
    channels?: string[],
    targetCurrency?: string,
    categories?: string[],
    subCategories?: string[],
    models?: string[],
    probabilityIds?: number[],
    lostReasons?: string[],
    projectNames?: string[]
  ): Observable<any> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    if (regionIds && regionIds.length > 0) {
      params = params.set('regionIds', regionIds.join(','));
    }
    if (countryIds && countryIds.length > 0) {
      params = params.set('countryIds', countryIds.join(','));
    }
    if (yearIds && yearIds.length > 0) {
      params = params.set('yearIds', yearIds.join(','));
    }
    if (monthIds && monthIds.length > 0) {
      params = params.set('monthIds', monthIds.join(','));
    }
    if (quarterFilters && quarterFilters.length > 0) {
      params = params.set('quarterFilters', quarterFilters.join(','));
    }
    if (search) {
      params = params.set('search', search);
    }
    if (statusNames && statusNames.length > 0) {
      params = params.set('statusName', statusNames.join(','));
    }
    if (customerNames && customerNames.length > 0) {
      params = params.set('customerName', customerNames.join(','));
    }
    if (productTypes && productTypes.length > 0) {
      params = params.set('productTypes', productTypes.join(','));
    }
    if (channels && channels.length > 0) {
      params = params.set('channel', channels.join(','));
    }
    if (targetCurrency) {
      params = params.set('targetCurrency', targetCurrency);
    }
    if (categories && categories.length > 0) {
      params = params.set('categories', categories.join(','));
    }
    if (subCategories && subCategories.length > 0) {
      params = params.set('subCategories', subCategories.join(','));
    }
    if (models && models.length > 0) {
      params = params.set('models', models.join(','));
    }
    if (probabilityIds && probabilityIds.length > 0) {
      params = params.set('probabilityIds', probabilityIds.join(','));
    }
    if (lostReasons && lostReasons.length > 0) {
      params = params.set('lostReasons', lostReasons.join(','));
    }
    if (projectNames && projectNames.length > 0) {
      params = params.set('projectNames', projectNames.join(','));
    }

    return this.http
      .get<any>(`${PROJECT_MANAGEMENT_URL}/project-sales`, { params });
  }

  /**
   * Export project sales data to Excel
   */
  public exportProjectSales(
    regionIds?: number[],
    countryIds?: number[],
    yearIds?: number[],
    monthIds?: number[],
    quarterFilters?: string[],
    search?: string,
    statusNames?: string[],
    customerNames?: string[],
    productTypes?: string[],
    channels?: string[],
    targetCurrency?: string,
    categories?: string[],
    subCategories?: string[],
    models?: string[],
    probabilityIds?: number[],
    lostReasons?: string[],
    projectNames?: string[]
  ): Observable<Blob> {
    let params = new HttpParams();

    if (regionIds && regionIds.length > 0) {
      params = params.set('regionIds', regionIds.join(','));
    }
    if (countryIds && countryIds.length > 0) {
      params = params.set('countryIds', countryIds.join(','));
    }
    if (yearIds && yearIds.length > 0) {
      params = params.set('yearIds', yearIds.join(','));
    }
    if (monthIds && monthIds.length > 0) {
      params = params.set('monthIds', monthIds.join(','));
    }
    if (quarterFilters && quarterFilters.length > 0) {
      params = params.set('quarterFilters', quarterFilters.join(','));
    }
    if (search) {
      params = params.set('search', search);
    }
    if (statusNames && statusNames.length > 0) {
      params = params.set('statusName', statusNames.join(','));
    }
    if (customerNames && customerNames.length > 0) {
      params = params.set('customerName', customerNames.join(','));
    }
    if (productTypes && productTypes.length > 0) {
      params = params.set('productTypes', productTypes.join(','));
    }
    if (channels && channels.length > 0) {
      params = params.set('channel', channels.join(','));
    }
    if (targetCurrency) {
      params = params.set('targetCurrency', targetCurrency);
    }
    if (categories && categories.length > 0) {
      params = params.set('categories', categories.join(','));
    }
    if (subCategories && subCategories.length > 0) {
      params = params.set('subCategories', subCategories.join(','));
    }
    if (models && models.length > 0) {
      params = params.set('models', models.join(','));
    }
    if (probabilityIds && probabilityIds.length > 0) {
      params = params.set('probabilityIds', probabilityIds.join(','));
    }
    if (lostReasons && lostReasons.length > 0) {
      params = params.set('lostReasons', lostReasons.join(','));
    }
    if (projectNames && projectNames.length > 0) {
      params = params.set('projectNames', projectNames.join(','));
    }

    return this.http
      .get(`${PROJECT_MANAGEMENT_URL}/project-sales/downloadExcelReport`, {
        params,
        responseType: 'blob'
      });
  }

  /**
   * Get unique models from project sales
   */
  public getModels(countryIds?: number[]): Observable<string[]> {
    let params = new HttpParams();
    if (countryIds && countryIds.length > 0) {
      params = params.set('countryIds', countryIds.join(','));
    }
    return this.http
      .get<{ result: string[] | any }>(`${PROJECT_MANAGEMENT_URL}/project-sales/models`, { params })
      .pipe(
        map(response => response?.result || response || []),
        catchError(this.errorHandler.handleError)
      );
  }

  /**
   * Get Analytical Dashboard KPI data
   */
  public getAnalyticalKPIData(
    countryIds?: number[],
    customerIds?: number[],
    categories?: string[],
    subCategories?: string[],
    statuses?: string[],
    probabilityIds?: number[],
    salesPersonIds?: number[],
    targetCurrency?: string,
    selectedYears?: number[],
    targetDeliveryYears?: number[]
  ): Observable<AnalyticalKPIData> {
    let params = new HttpParams();
    if (countryIds && countryIds.length > 0) {
      params = params.set('countryIds', countryIds.join(','));
    }
    if (customerIds && customerIds.length > 0) {
      params = params.set('customerIds', customerIds.join(','));
    }
    if (categories && categories.length > 0) {
      params = params.set('categories', categories.join(','));
    }
    if (subCategories && subCategories.length > 0) {
      params = params.set('subCategories', subCategories.join(','));
    }
    if (statuses && statuses.length > 0) {
      params = params.set('statuses', statuses.join(','));
    }
    if (probabilityIds && probabilityIds.length > 0) {
      params = params.set('probabilityIds', probabilityIds.join(','));
    }
    if (salesPersonIds && salesPersonIds.length > 0) {
      params = params.set('salesPersonIds', salesPersonIds.join(','));
    }
    if (targetCurrency) {
      params = params.set('targetCurrency', targetCurrency);
    }
    if (selectedYears && selectedYears.length > 0) {
      params = params.set('selectedYears', selectedYears.join(','));
    }
    if (targetDeliveryYears && targetDeliveryYears.length > 0) {
      params = params.set('targetDeliveryYears', targetDeliveryYears.join(','));
    }
    return this.http
      .get<AnalyticalKPIData>(`${PROJECT_MANAGEMENT_URL}/analytical-dashboard/kpi`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get Year Wise Summary data
   */
  public getYearWiseData(
    countryIds?: number[],
    customerIds?: number[],
    categories?: string[],
    subCategories?: string[],
    probabilityIds?: number[],
    salesPersonIds?: number[],
    targetCurrency?: string,
    selectedYears?: number[],
    targetDeliveryYears?: number[]
  ): Observable<YearWiseData> {
    let params = new HttpParams();
    if (countryIds && countryIds.length > 0) {
      params = params.set('countryIds', countryIds.join(','));
    }
    if (customerIds && customerIds.length > 0) {
      params = params.set('customerIds', customerIds.join(','));
    }
    if (categories && categories.length > 0) {
      params = params.set('categories', categories.join(','));
    }
    if (subCategories && subCategories.length > 0) {
      params = params.set('subCategories', subCategories.join(','));
    }
    if (probabilityIds && probabilityIds.length > 0) {
      params = params.set('probabilityIds', probabilityIds.join(','));
    }
    if (salesPersonIds && salesPersonIds.length > 0) {
      params = params.set('salesPersonIds', salesPersonIds.join(','));
    }
    if (targetCurrency) {
      params = params.set('targetCurrency', targetCurrency);
    }
    if (selectedYears && selectedYears.length > 0) {
      params = params.set('selectedYears', selectedYears.join(','));
    }
    if (targetDeliveryYears && targetDeliveryYears.length > 0) {
      params = params.set('targetDeliveryYears', targetDeliveryYears.join(','));
    }
    return this.http
      .get<YearWiseData>(`${PROJECT_MANAGEMENT_URL}/analytical-dashboard/year-wise`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get Category Wise Summary data for pie chart
   */
  public getCategoryWiseData(
    countryIds?: number[],
    customerIds?: number[],
    categories?: string[],
    subCategories?: string[],
    statuses?: string[],
    probabilityIds?: number[],
    salesPersonIds?: number[],
    targetCurrency?: string,
    selectedYears?: number[],
    targetDeliveryYears?: number[]
  ): Observable<CategoryWiseData[]> {
    let params = new HttpParams();
    if (countryIds && countryIds.length > 0) {
      params = params.set('countryIds', countryIds.join(','));
    }
    if (customerIds && customerIds.length > 0) {
      params = params.set('customerIds', customerIds.join(','));
    }
    if (categories && categories.length > 0) {
      params = params.set('categories', categories.join(','));
    }
    if (subCategories && subCategories.length > 0) {
      params = params.set('subCategories', subCategories.join(','));
    }
    if (statuses && statuses.length > 0) {
      params = params.set('statuses', statuses.join(','));
    }
    if (probabilityIds && probabilityIds.length > 0) {
      params = params.set('probabilityIds', probabilityIds.join(','));
    }
    if (salesPersonIds && salesPersonIds.length > 0) {
      params = params.set('salesPersonIds', salesPersonIds.join(','));
    }
    if (targetCurrency) {
      params = params.set('targetCurrency', targetCurrency);
    }
    if (selectedYears && selectedYears.length > 0) {
      params = params.set('selectedYears', selectedYears.join(','));
    }
    if (targetDeliveryYears && targetDeliveryYears.length > 0) {
      params = params.set('targetDeliveryYears', targetDeliveryYears.join(','));
    }
    return this.http
      .get<CategoryWiseData[]>(`${PROJECT_MANAGEMENT_URL}/analytical-dashboard/category-wise`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get SubCategory Wise Summary data for pie chart
   */
  public getSubCategoryWiseData(
    countryIds?: number[],
    customerIds?: number[],
    categories?: string[],
    subCategories?: string[],
    statuses?: string[],
    probabilityIds?: number[],
    salesPersonIds?: number[],
    targetCurrency?: string,
    selectedYears?: number[],
    targetDeliveryYears?: number[]
  ): Observable<CategoryWiseData[]> {
    let params = new HttpParams();
    if (countryIds && countryIds.length > 0) {
      params = params.set('countryIds', countryIds.join(','));
    }
    if (customerIds && customerIds.length > 0) {
      params = params.set('customerIds', customerIds.join(','));
    }
    if (categories && categories.length > 0) {
      params = params.set('categories', categories.join(','));
    }
    if (subCategories && subCategories.length > 0) {
      params = params.set('subCategories', subCategories.join(','));
    }
    if (statuses && statuses.length > 0) {
      params = params.set('statuses', statuses.join(','));
    }
    if (probabilityIds && probabilityIds.length > 0) {
      params = params.set('probabilityIds', probabilityIds.join(','));
    }
    if (salesPersonIds && salesPersonIds.length > 0) {
      params = params.set('salesPersonIds', salesPersonIds.join(','));
    }
    if (targetCurrency) {
      params = params.set('targetCurrency', targetCurrency);
    }
    if (selectedYears && selectedYears.length > 0) {
      params = params.set('selectedYears', selectedYears.join(','));
    }
    if (targetDeliveryYears && targetDeliveryYears.length > 0) {
      params = params.set('targetDeliveryYears', targetDeliveryYears.join(','));
    }
    return this.http
      .get<CategoryWiseData[]>(`${PROJECT_MANAGEMENT_URL}/analytical-dashboard/subcategory-wise`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get distinct categories from lead_products table
   */
  public getDistinctCategories(countryIds?: number[]): Observable<DropdownOption[]> {
    let params = new HttpParams();
    if (countryIds && countryIds.length > 0) {
      params = params.set('countryIds', countryIds.join(','));
    }
    return this.http
      .get<DropdownOption[]>(`${PROJECT_MANAGEMENT_URL}/analytical-dashboard/distinct-categories`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get distinct subcategories from lead_products table
   */
  public getDistinctSubCategories(countryIds?: number[], categories?: string[]): Observable<DropdownOption[]> {
    let params = new HttpParams();
    if (countryIds && countryIds.length > 0) {
      params = params.set('countryIds', countryIds.join(','));
    }
    if (categories && categories.length > 0) {
      params = params.set('categories', categories.join(','));
    }
    return this.http
      .get<DropdownOption[]>(`${PROJECT_MANAGEMENT_URL}/analytical-dashboard/distinct-subcategories`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }
  /**
   * Get unique product types from lead_products
   */
  public getProductTypes(countryIds?: number[]): Observable<string[]> {
    let params = new HttpParams();
    if (countryIds && countryIds.length > 0) {
      params = params.set('countryIds', countryIds.join(','));
    }
    return this.http
      .get<{ result: string[] }>(`${PROJECT_MANAGEMENT_URL}/project-sales/product-types`, { params })
      .pipe(
        map(response => response?.result || []),
        catchError(this.errorHandler.handleError)
      );
  }
  /**
   * Get distinct years from lead creation dates and targets
   */
  public getDistinctCreatedYears(countryIds?: number[]): Observable<number[]> {
    let params = new HttpParams();
    if (countryIds && countryIds.length > 0) {
      params = params.set('countryIds', countryIds.join(','));
    }
    return this.http
      .get<number[]>(`${PROJECT_MANAGEMENT_URL}/analytical-dashboard/distinct-created-years`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get distinct years from lead target delivery dates
   */
  public getDistinctTargetDeliveryYears(countryIds?: number[]): Observable<number[]> {
    let params = new HttpParams();
    if (countryIds && countryIds.length > 0) {
      params = params.set('countryIds', countryIds.join(','));
    }
    return this.http
      .get<number[]>(`${PROJECT_MANAGEMENT_URL}/analytical-dashboard/distinct-target-delivery-years`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }
  /**
   * Get distinct customers from lead table
   */
  public getDistinctCustomers(countryIds?: number[]): Observable<DropdownOption[]> {
    let params = new HttpParams();
    if (countryIds && countryIds.length > 0) {
      params = params.set('countryIds', countryIds.join(','));
    }
    return this.http
      .get<DropdownOption[]>(`${PROJECT_MANAGEMENT_URL}/analytical-dashboard/distinct-customers`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get distinct years from lead target delivery dates for project sales
   */
  public getDistinctTargetDeliveryYearsForProjectSales(countryIds?: number[]): Observable<number[]> {
    let params = new HttpParams();
    if (countryIds && countryIds.length > 0) {
      params = params.set('countryIds', countryIds.join(','));
    }
    return this.http
      .get<number[]>(`${PROJECT_MANAGEMENT_URL}/project-sales/target-delivery-years`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Fetch SAP Net Sales Data from Databricks
   */
  public getSapNetSalesData(countries?: string[], years?: number[], lastMonthStr?: string): Observable<any> {
    let params = new HttpParams();
    if (countries && countries.length > 0) {
      params = params.set('countries', countries.join(','));
    }
    if (years && years.length > 0) {
      params = params.set('years', years.join(','));
    }
    if (lastMonthStr) {
      params = params.set('lastMonthStr', lastMonthStr);
    }
    return this.http
      .get<any>(`${PROJECT_MANAGEMENT_URL}/sap-data/net-sales`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Fetch SAP Quantity Data from Databricks
   */
  public getSapQuantityData(countries?: string[], years?: number[], lastMonthStr?: string): Observable<any> {
    let params = new HttpParams();
    if (countries && countries.length > 0) {
      params = params.set('countries', countries.join(','));
    }
    if (years && years.length > 0) {
      params = params.set('years', years.join(','));
    }
    if (lastMonthStr) {
      params = params.set('lastMonthStr', lastMonthStr);
    }
    return this.http
      .get<any>(`${PROJECT_MANAGEMENT_URL}/sap-data/quantity`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Sync both SAP Value and Quantity data from Databricks to Postgres
   */
  public syncSapData(countries?: string[], years?: number[], lastMonthStr?: string): Observable<any> {
    return this.http
      .post<any>(`${PROJECT_MANAGEMENT_URL}/sap-data/sync-both`, { countries, years, lastMonthStr })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get current progress of SAP sync
   */
  public getSapSyncProgress(): Observable<any> {
    return this.http
      .get<any>(`${PROJECT_MANAGEMENT_URL}/sap-data/sync-progress`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Fetch SAP Exchange Rates Data from Databricks
   */
  public getExchangeRates(): Observable<any> {
    return this.http
      .get<any>(`${PROJECT_MANAGEMENT_URL}/sap-data/exchange-rates`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Fetch distinct lost reasons from backend
   */
  public getLostReasons(countryIds?: number[]): Observable<string[]> {
    let params = new HttpParams();
    if (countryIds && countryIds.length > 0) {
      params = params.set('countryIds', countryIds.join(','));
    }
    return this.http
      .get<string[]>(`${PROJECT_MANAGEMENT_URL}/project-sales/lost-reasons`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Fetch distinct project names from backend
   */
  public getProjectNames(countryIds?: number[]): Observable<string[]> {
    let params = new HttpParams();
    if (countryIds && countryIds.length > 0) {
      params = params.set('countryIds', countryIds.join(','));
    }
    return this.http
      .get<string[]>(`${PROJECT_MANAGEMENT_URL}/project-sales/project-names`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get allowed country IDs configured in the backend settings
   */
  public getAllowedCountries(): Observable<any> {
    return this.http
      .get<any>(`${PROJECT_MANAGEMENT_URL}/allowed-countries/getAll`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Update the allowed country IDs configuration
   */
  public updateAllowedCountries(countryIds: number[]): Observable<any> {
    return this.http
      .post<any>(`${PROJECT_MANAGEMENT_URL}/allowed-countries/update`, { countryIds })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public filterUserCountries(userCountries: any[]): Observable<any[]> {
    return this.getAllowedCountries().pipe(
      map((res: any) => {
        let allowedIds = res?.result || res || [];
        if (allowedIds && typeof allowedIds === 'object' && !Array.isArray(allowedIds)) {
          allowedIds = allowedIds.result || [];
        }
        if (!Array.isArray(allowedIds) || allowedIds.length === 0) {
          return userCountries;
        }
        return userCountries.filter((c: any) => allowedIds.includes(Number(c.id)));
      }),
      catchError(() => of(userCountries))
    );
  }

  public syncChinaLeads(accessToken?: string): Observable<any> {
    return this.http
      .post<any>(`${PROJECT_MANAGEMENT_URL}/china-leads/sync`, { accessToken })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public triggerChinaCronSync(): Observable<any> {
    return this.http
      .post<any>(`${PROJECT_MANAGEMENT_URL}/china-leads/trigger-cron`, {})
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getChinaSyncProgress(): Observable<any> {
    return this.http
      .get<any>(`${PROJECT_MANAGEMENT_URL}/china-leads/sync-progress`)
      .pipe(catchError(this.errorHandler.handleError));
  }
}


